/**
 * HUD text size (08 §4). The menus have had x1 / x1.5 / x2 since round two; the in-mission HUD never honoured
 * it, and had never been looked at at anything but x1. Only the PROSE scales - the standing orders, the
 * mission's own timer, the extraction line, the banners and the bottom prompt - because those are read as
 * sentences. The instruments (the clock, the ammunition figure, the alert pips, the 1987 box) keep their size:
 * they are read at a glance, and they are laid out to the pixel on a 640x360 canvas.
 *
 * The widths below are the numbers the panel is actually given (objectivePanel.ts sets them), so a test can
 * check a real mission's orders at all three sizes without a layout engine: `wrapRows` is the same greedy
 * word wrap the browser does on a monospace face.
 */

export type HudTextSize = 1 | 1.5 | 2;

/** Every size, for a test that must cover all three. */
export const HUD_TEXT_SIZES: readonly HudTextSize[] = [1, 1.5, 2];

/** The root class that carries the size; empty at x1, which needs none. */
export const hudTextClass = (size: HudTextSize): string =>
  (size === 1.5 ? 'hud-text-15' : size === 2 ? 'hud-text-2' : '');

export const asTextSize = (value: number): HudTextSize => (value === 1.5 ? 1.5 : value === 2 ? 2 : 1);

/** Font size at x1, logical px, of each piece of HUD prose; the size classes multiply by `--hud-ts`. */
export const HUD_FONT = { orders: 6, timer: 8, sub: 8, banner: 9, prompt: 9, osprey: 8 } as const;
export type HudProse = keyof typeof HUD_FONT;

export const fontPx = (line: HudProse, size: HudTextSize): number => HUD_FONT[line] * size;

/** Line box of a piece of prose, logical px: the orders sit tight, everything else on the root's 10 px. */
export const lineHeightPx = (line: HudProse, size: HudTextSize): number => (line === 'orders' ? 8 : 10) * size;

/**
 * How wide the standing orders may run, logical px. The collapsed block is one rule and stays out of the way;
 * in full, for the first seconds of the mission or while the key is held, it may take the left of the screen.
 * They do not simply scale: at x2 a proportional block would be half the canvas wide and still wrap as badly.
 */
export function ordersWidth(size: HudTextSize, full: boolean): number {
  if (size === 2) return full ? 400 : 300;
  if (size === 1.5) return full ? 360 : 250;
  return full ? 300 : 190;
}

/**
 * Rows the folded block may show before it is clamped with an ellipsis (objectivePanel.css). One mission's
 * rule runs to 189 characters, which is four rows even at x1 and would put the panel back where it started;
 * folded, it is a reminder, and the whole of it is one key away.
 */
export const COLLAPSED_ROWS = 3;

/** A monospace advance is 0.6 em on every face the HUD asks for, plus the tracking the stylesheet adds. */
export const CHAR_ASPECT = 0.6;

export const advancePx = (font: number, letterSpacing: number): number => font * CHAR_ASPECT + letterSpacing;

/** Characters that fit on one line of `widthPx`. At least one, or wrapping would never end. */
export const charsPerLine = (widthPx: number, font: number, letterSpacing: number): number =>
  Math.max(1, Math.floor(widthPx / advancePx(font, letterSpacing)));

/**
 * Rows this text takes at that width: newlines break (the orders block is `white-space: pre-line`) and words
 * wrap, greedily, as a browser does. A word longer than the line gets a row of its own and overflows - the
 * HUD has no such word, and a test that finds one has found a bug.
 */
export function wrapRows(text: string, widthPx: number, font: number, letterSpacing = 0.5): number {
  const columns = charsPerLine(widthPx, font, letterSpacing);
  let rows = 0;
  for (const paragraph of text.split('\n')) {
    let used = 0;
    rows += 1;
    for (const word of paragraph.split(' ').filter((w) => w !== '')) {
      const width = word.length;
      if (used === 0) used = width;
      else if (used + 1 + width <= columns) used += 1 + width;
      else {
        rows += 1;
        used = width;
      }
    }
  }
  return rows;
}

/** How tall that text stands in the panel, logical px. */
export const blockHeight = (text: string, widthPx: number, line: HudProse, size: HudTextSize): number =>
  wrapRows(text, widthPx, fontPx(line, size)) * lineHeightPx(line, size);

/** How tall the FOLDED orders stand: the same, clamped to `COLLAPSED_ROWS`. */
export const collapsedHeight = (text: string, size: HudTextSize): number =>
  Math.min(COLLAPSED_ROWS, wrapRows(text, ordersWidth(size, false), fontPx('orders', size)))
  * lineHeightPx('orders', size);
